// Extracted from ProductsPage.js. Hardware System
// variant selector dialog (Tandems, Hinges, Telescopic, Sliding,
// Pullouts, Baskets …) with type-specific sub-selectors and inner
// multi-select cart.
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Plus, X } from 'lucide-react';
import { formatINR } from '../../../lib/store';

export function HardwareSystemSelectorDialog({
  group,
  onClose,
  selectedVariant,
  setSelectedVariant,
  tandemDepth,
  setTandemDepth,
  hingeMountType,
  setHingeMountType,
  telescopicFeature,
  setTelescopicFeature,
  slidingType,
  setSlidingType,
  quantityInput,
  setQuantityInput,
  cart,
  onAddToCart,
  onRemoveFromCart,
  onAddAll,
  onSingleAdd,
}) {
  return (
    <Dialog open={!!group} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="border-heda-line bg-white sm:rounded-3xl max-w-lg" data-testid="hardware-system-variant-dialog">
        <DialogHeader>
          <DialogTitle className="text-heda-ink">
            {group?.hardware_system_type === 'tandem' ? 'Select Depth & Height' : 'Select Variant & Add'}
          </DialogTitle>
        </DialogHeader>

        {group && (
          <div className="space-y-6">
            {/* Product Info */}
            <div className="p-4 bg-heda-olive-tint rounded-xl border border-heda-olive/30">
              <p className="font-bold text-heda-ink">{group.brand}</p>
              <p className="text-sm text-heda-sub">{group.name}</p>
              <div className="flex flex-wrap gap-1.5 mt-2">
                <span className="text-xs bg-heda-olive-tint text-heda-olive-dark px-2 py-0.5 rounded-lg font-medium capitalize">
                  {group.hardware_system_type?.replace(/_/g, ' ')}
                </span>
                {group.load_capacity && (
                  <span className="text-xs bg-heda-cream px-2 py-0.5 rounded-lg">{group.load_capacity}</span>
                )}
                {group.feature && (
                  <span className="text-xs bg-heda-cream px-2 py-0.5 rounded-lg">{group.feature}</span>
                )}
                {group.color && (
                  <span className="text-xs bg-heda-cream px-2 py-0.5 rounded-lg">{group.color}</span>
                )}
              </div>
            </div>

            {/* TANDEM: Depth selector on left, Heights on right */}
            {group.hardware_system_type === 'tandem' && (() => {
              const depths = [...new Set(group.hardware_system_variants.map(v => v.depth).filter(Boolean))].sort((a, b) => parseFloat(a) - parseFloat(b));
              const heightsForDepth = group.hardware_system_variants.filter(v => v.depth === tandemDepth);

              return (
                <div className="p-4 bg-white rounded-xl border border-heda-line">
                  <div className="flex gap-6">
                    {/* Depth selector */}
                    <div className="w-1/3">
                      <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute mb-3 block">Depth (mm)</Label>
                      <div className="space-y-2">
                        {depths.map(depth => (
                          <button
                            key={depth}
                            onClick={() => { setTandemDepth(depth); setSelectedVariant(null); }}
                            className={`w-full px-3 py-2 text-sm rounded-xl border transition-all text-left ${
                              tandemDepth === depth
                                ? 'border-heda-ink bg-heda-ink text-white font-medium'
                                : 'border-heda-line bg-white hover:border-heda-olive/50'
                            }`}
                          >
                            {depth}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Heights for selected depth */}
                    <div className="flex-1">
                      <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute mb-3 block">
                        Heights for {tandemDepth?.replace(/mm$/i, '')}mm depth ({heightsForDepth.length} available)
                      </Label>
                      <div className="grid grid-cols-2 gap-3 max-h-48 overflow-y-auto">
                        {heightsForDepth.map((hsv, i) => {
                          const isSelected = selectedVariant === hsv;
                          // Clean height - remove trailing 'mm' if present to avoid duplication
                          const cleanHeight = hsv.height?.toString().replace(/mm$/i, '') || '';
                          return (
                            <button
                              key={i}
                              onClick={() => setSelectedVariant(hsv)}
                              className={`flex flex-col items-center px-3 py-3 rounded-xl border transition-all ${
                                isSelected
                                  ? 'border-heda-ink bg-heda-ink text-white'
                                  : 'border-heda-line bg-white hover:border-heda-olive/50'
                              }`}
                            >
                              <span className={`text-sm font-semibold ${isSelected ? 'text-white' : 'text-heda-ink'}`}>{cleanHeight}mm</span>
                              <span className={`text-xs ${isSelected ? 'text-white/80' : 'text-heda-olive-dark'}`}>
                                {formatINR(hsv.rate)}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* HINGE: Mount type selector (Clip-on/Slide-on) first, then Crank options */}
            {group.hardware_system_type === 'hinge' && (() => {
              const mountTypes = [...new Set(group.hardware_system_variants.map(v => v.hinge_mount).filter(Boolean))];
              const cranksForMountType = group.hardware_system_variants.filter(v => v.hinge_mount === hingeMountType);

              return (
                <div className="space-y-4">
                  {/* Mount Type selector */}
                  <div>
                    <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute mb-2 block">Select Type</Label>
                    <div className="flex gap-2">
                      {mountTypes.map(mountType => (
                        <button
                          key={mountType}
                          onClick={() => { setHingeMountType(mountType); setSelectedVariant(null); }}
                          className={`flex-1 px-4 py-3 text-sm rounded-xl border transition-all font-medium ${
                            hingeMountType === mountType
                              ? 'border-heda-ink bg-heda-ink text-white'
                              : 'border-heda-line bg-white hover:border-heda-olive/50'
                          }`}
                        >
                          {mountType}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Crank options for selected mount type */}
                  {hingeMountType && (
                    <div>
                      <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute mb-2 block">
                        Select Crank ({cranksForMountType.length} available)
                      </Label>
                      <div className="flex flex-wrap gap-2">
                        {cranksForMountType.map((hsv, i) => {
                          const isSelected = selectedVariant === hsv;
                          return (
                            <button
                              key={i}
                              onClick={() => setSelectedVariant(hsv)}
                              className={`flex flex-col items-center px-4 py-3 rounded-xl border transition-all min-w-[100px] ${
                                isSelected
                                  ? 'border-heda-ink bg-heda-ink text-white'
                                  : 'border-heda-line bg-white hover:border-heda-olive/50'
                              }`}
                            >
                              <span className={`text-base font-semibold ${isSelected ? 'text-white' : 'text-heda-ink'}`}>{hsv.size_text || 'Standard'}</span>
                              <span className={`text-sm ${isSelected ? 'text-white/80' : 'text-heda-olive-dark'}`}>
                                {formatINR(hsv.rate)}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* TELESCOPIC: Feature type selector first (Regular, Soft Close, etc.), then sizes */}
            {group.hardware_system_type === 'telescopic' && (() => {
              const featureTypes = [...new Set(group.hardware_system_variants.map(v => v.feature).filter(Boolean))];
              const sizesForFeature = group.hardware_system_variants.filter(v => v.feature === telescopicFeature);

              // If no feature types defined, show all variants
              if (featureTypes.length === 0) {
                return (
                  <div className="p-4 bg-white rounded-xl border border-heda-line">
                    <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute mb-3 block">
                      Select Size ({group.hardware_system_variants.length} available)
                    </Label>
                    <div className="grid grid-cols-2 gap-3 max-h-48 overflow-y-auto">
                      {group.hardware_system_variants.map((hsv, i) => {
                        const displayText = hsv.size_text || `${hsv.depth || ''} x ${hsv.height || ''}`.replace(/^x+|x+$/g, '').trim() || `Variant ${i + 1}`;
                        const isSelected = selectedVariant === hsv;
                        return (
                          <button
                            key={i}
                            onClick={() => setSelectedVariant(hsv)}
                            className={`flex flex-col items-center px-3 py-3 rounded-xl border transition-all ${
                              isSelected
                                ? 'border-heda-ink bg-heda-ink text-white'
                                : 'border-heda-line bg-white hover:border-heda-olive/50'
                            }`}
                          >
                            <span className={`text-sm font-semibold ${isSelected ? 'text-white' : 'text-heda-ink'}`}>{displayText}</span>
                            <span className={`text-xs ${isSelected ? 'text-white/80' : 'text-heda-olive-dark'}`}>
                              {formatINR(hsv.rate)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              }

              return (
                <div className="p-4 bg-white rounded-xl border border-heda-line space-y-4">
                  {/* Feature Type selector */}
                  <div>
                    <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute mb-3 block">Select Type</Label>
                    <div className="flex flex-wrap gap-2">
                      {featureTypes.map(feature => (
                        <button
                          key={feature}
                          onClick={() => { setTelescopicFeature(feature); setSelectedVariant(null); }}
                          className={`px-4 py-2 text-sm rounded-xl border transition-all font-medium ${
                            telescopicFeature === feature
                              ? 'border-heda-ink bg-heda-ink text-white'
                              : 'border-heda-line bg-white hover:border-heda-olive/50'
                          }`}
                        >
                          {feature}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Sizes for selected feature type */}
                  {telescopicFeature && sizesForFeature.length > 0 && (
                    <div>
                      <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute mb-3 block">
                        Select Size ({sizesForFeature.length} available)
                      </Label>
                      <div className="grid grid-cols-3 gap-3 max-h-48 overflow-y-auto">
                        {sizesForFeature.map((hsv, i) => {
                          const displayText = hsv.size_text || `${hsv.depth || ''} x ${hsv.height || ''}`.replace(/^x+|x+$/g, '').trim() || `Size ${i + 1}`;
                          const isSelected = selectedVariant === hsv;
                          return (
                            <button
                              key={i}
                              onClick={() => setSelectedVariant(hsv)}
                              className={`flex flex-col items-center px-3 py-3 rounded-xl border transition-all ${
                                isSelected
                                  ? 'border-heda-ink bg-heda-ink text-white'
                                  : 'border-heda-line bg-white hover:border-heda-olive/50'
                              }`}
                            >
                              <span className={`text-sm font-semibold ${isSelected ? 'text-white' : 'text-heda-ink'}`}>{displayText}</span>
                              <span className={`text-xs ${isSelected ? 'text-white/80' : 'text-heda-olive-dark'}`}>
                                {formatINR(hsv.rate)}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* SLIDING: Sliding type selector (Top/Bottom Sliding), then sizes */}
            {group.hardware_system_type === 'sliding' && (() => {
              const slidingTypes = [...new Set(group.hardware_system_variants.map(v => v.sliding_type).filter(Boolean))];
              const sizesForSlidingType = group.hardware_system_variants.filter(v => v.sliding_type === slidingType);

              // If no sliding types defined, show all variants
              if (slidingTypes.length === 0) {
                return (
                  <div className="p-4 bg-white rounded-xl border border-heda-line">
                    <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute mb-3 block">
                      Select Size ({group.hardware_system_variants.length} available)
                    </Label>
                    <div className="grid grid-cols-3 gap-3 max-h-48 overflow-y-auto">
                      {group.hardware_system_variants.map((hsv, i) => {
                        const displayText = hsv.size_text || `${hsv.size_ft || ''} ft`.trim() || `Size ${i + 1}`;
                        const isSelected = selectedVariant === hsv;
                        return (
                          <button
                            key={i}
                            onClick={() => setSelectedVariant(hsv)}
                            className={`flex flex-col items-center px-3 py-3 rounded-xl border transition-all ${
                              isSelected
                                ? 'border-heda-ink bg-heda-ink text-white'
                                : 'border-heda-line bg-white hover:border-heda-olive/50'
                            }`}
                          >
                            <span className={`text-sm font-semibold ${isSelected ? 'text-white' : 'text-heda-ink'}`}>{displayText}</span>
                            <span className={`text-xs ${isSelected ? 'text-white/80' : 'text-heda-olive-dark'}`}>
                              {formatINR(hsv.rate)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              }

              return (
                <div className="p-4 bg-white rounded-xl border border-heda-line space-y-4">
                  {/* Sliding Type selector */}
                  <div>
                    <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute mb-3 block">Select Sliding Type</Label>
                    <div className="flex flex-wrap gap-2">
                      {slidingTypes.map(st => (
                        <button
                          key={st}
                          onClick={() => { setSlidingType(st); setSelectedVariant(null); }}
                          className={`px-4 py-2 text-sm rounded-xl border transition-all font-medium ${
                            slidingType === st
                              ? 'border-heda-ink bg-heda-ink text-white'
                              : 'border-heda-line bg-white hover:border-heda-olive/50'
                          }`}
                        >
                          {st}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Sizes for selected sliding type */}
                  {slidingType && sizesForSlidingType.length > 0 && (
                    <div>
                      <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute mb-3 block">
                        Select Size ({sizesForSlidingType.length} available)
                      </Label>
                      <div className="grid grid-cols-3 gap-3 max-h-48 overflow-y-auto">
                        {sizesForSlidingType.map((hsv, i) => {
                          const displayText = hsv.size_text || `${hsv.size_ft || ''} ft`.trim() || `Size ${i + 1}`;
                          const isSelected = selectedVariant === hsv;
                          return (
                            <button
                              key={i}
                              onClick={() => setSelectedVariant(hsv)}
                              className={`flex flex-col items-center px-3 py-3 rounded-xl border transition-all ${
                                isSelected
                                  ? 'border-heda-ink bg-heda-ink text-white'
                                  : 'border-heda-line bg-white hover:border-heda-olive/50'
                              }`}
                            >
                              <span className={`text-sm font-semibold ${isSelected ? 'text-white' : 'text-heda-ink'}`}>{displayText}</span>
                              <span className={`text-xs ${isSelected ? 'text-white/80' : 'text-heda-olive-dark'}`}>
                                {formatINR(hsv.rate)}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* PULLOUT: Clean list-based variant selector */}
            {group.hardware_system_type === 'pullout' && (
              <div className="bg-white rounded-xl border border-heda-line">
                <div className="p-3 border-b border-heda-line bg-heda-cream">
                  <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">
                    Select Size ({group.hardware_system_variants.length} available)
                  </Label>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {group.hardware_system_variants.map((hsv, i) => {
                    // Helper to check if a value is valid
                    const isValid = (val) => val && val !== 'mm' && val.replace(/mm$/i, '').trim();

                    // Build dimensions display
                    const dims = [];
                    if (isValid(hsv.width)) dims.push(`W: ${hsv.width.replace(/mm$/i, '')}mm`);
                    if (isValid(hsv.depth)) dims.push(`D: ${hsv.depth.replace(/mm$/i, '')}mm`);
                    if (isValid(hsv.height)) dims.push(`H: ${hsv.height.replace(/mm$/i, '')}mm`);

                    let displayText = dims.join('  •  ');
                    if (!displayText && hsv.size_text && hsv.size_text !== 'mm') {
                      displayText = hsv.size_text;
                    }
                    displayText = displayText || `Variant ${i + 1}`;

                    const isSelected = selectedVariant === hsv;
                    return (
                      <button
                        key={i}
                        onClick={() => setSelectedVariant(hsv)}
                        className={`w-full flex items-center justify-between px-4 py-3 border-b border-heda-line last:border-b-0 transition-all ${
                          isSelected
                            ? 'bg-heda-ink text-white'
                            : 'bg-white hover:bg-heda-cream/60'
                        }`}
                      >
                        <span className={`text-sm font-medium ${isSelected ? 'text-white' : 'text-heda-ink'}`}>
                          {displayText}
                        </span>
                        <span className={`text-sm font-semibold ${isSelected ? 'text-white' : 'text-heda-olive-dark'}`}>
                          {formatINR(hsv.rate)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* OTHER TYPES (Basket, Accessory, etc.): Simple variant selector */}
            {!['tandem', 'hinge', 'telescopic', 'sliding', 'pullout'].includes(group.hardware_system_type) && (
              <div>
                <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">
                  Select Variant ({group.hardware_system_variants.length} available)
                </Label>
                <div className="mt-2 grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                  {group.hardware_system_variants.map((hsv, i) => {
                    let displayText = hsv.size_text;
                    if (!displayText) {
                      const parts = [hsv.depth, hsv.height].filter(Boolean);
                      displayText = parts.join(' x ');
                    }
                    displayText = displayText?.replace(/^x+|x+$/g, '').trim() || `Variant ${i + 1}`;

                    const isSelected = selectedVariant === hsv;
                    return (
                      <button
                        key={i}
                        onClick={() => setSelectedVariant(hsv)}
                        className={`flex flex-col items-center justify-center px-3 py-2 rounded-xl border transition-all ${
                          isSelected
                            ? 'border-heda-ink bg-heda-ink text-white'
                            : 'border-heda-line bg-white hover:border-heda-olive/50'
                        }`}
                        data-testid={`hardware-system-size-${i}`}
                      >
                        <span className="font-mono text-sm font-semibold">{displayText}</span>
                        <span className={`text-xs ${isSelected ? 'text-white/80' : 'text-heda-olive-dark'}`}>
                          {formatINR(hsv.rate)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Quantity & Add to Selection */}
            {selectedVariant && (
              <div className="flex items-end gap-4 pt-4 border-t border-heda-line">
                <div className="flex-1">
                  <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Quantity</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const newQty = Math.max(1, (parseInt(quantityInput) || 1) - 1);
                        setQuantityInput(String(newQty));
                      }}
                      className="rounded-xl h-9 w-9 bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold"
                    >-</Button>
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={quantityInput}
                      onChange={(e) => setQuantityInput(e.target.value.replace(/[^0-9]/g, ''))}
                      onBlur={() => {
                        if (!quantityInput || parseInt(quantityInput) < 1) {
                          setQuantityInput('1');
                        }
                      }}
                      className="rounded-xl bg-heda-cream/50 text-center h-9 w-16 font-bold"
                      data-testid="hardware-system-quantity-input"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const newQty = (parseInt(quantityInput) || 1) + 1;
                        setQuantityInput(String(newQty));
                      }}
                      className="rounded-xl h-9 w-9 bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold"
                    >+</Button>
                  </div>
                </div>
                <Button
                  onClick={onAddToCart}
                  className="rounded-xl bg-heda-gold hover:bg-heda-gold/90 text-white h-10 px-5"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add to Selection
                </Button>
              </div>
            )}

            {/* Cart / Selection Summary */}
            {cart.length > 0 && (
              <div className="border-t border-heda-line pt-4">
                <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute mb-2 block">
                  Your Selection ({cart.length} items)
                </Label>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {cart.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-heda-cream/60 rounded-lg">
                      <div>
                        <span className="text-sm font-medium">{item.sizeText}</span>
                        <span className="text-xs text-heda-mute ml-2">× {item.quantity}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-mono">{formatINR(item.rate * item.quantity)}</span>
                        <button
                          onClick={() => onRemoveFromCart(idx)}
                          className="text-red-700 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between items-center mt-2 pt-2 border-t border-heda-line">
                  <span className="text-sm font-semibold">Total:</span>
                  <span className="text-lg font-bold font-mono-data text-heda-olive-dark">
                    {formatINR(cart.reduce((sum, item) => sum + item.rate * item.quantity, 0))}
                  </span>
                </div>
              </div>
            )}

            {/* Add to Quotation Button */}
            <div className="pt-2">
              {cart.length > 0 ? (
                <Button
                  onClick={onAddAll}
                  className="rounded-xl w-full bg-heda-ink hover:bg-black text-white uppercase tracking-wider font-semibold h-12"
                  data-testid="confirm-hardware-system-add-btn"
                >
                  <Plus className="w-4 h-4 mr-2" /> Add {cart.length} Item(s) to Quotation
                </Button>
              ) : selectedVariant ? (
                <Button
                  onClick={onSingleAdd}
                  className="rounded-xl w-full bg-heda-ink hover:bg-black text-white uppercase tracking-wider font-semibold h-12"
                  data-testid="confirm-hardware-system-add-btn"
                >
                  <Plus className="w-4 h-4 mr-2" /> Add to Quotation
                </Button>
              ) : (
                <p className="text-center text-sm text-heda-mute">Select a variant to add</p>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
